import { useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { useTran } from "@/hooks";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
const Month_Numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const RANGE_SIZE = 12;
const currentYear = new Date().getFullYear();

function getRangeStart(year, minYear) {
  return Math.floor((year - minYear) / RANGE_SIZE) * RANGE_SIZE + minYear;
}
const getMonths = (monthNum, lng) => {
  if (!monthNum) return "";
  return new Date(2000, monthNum - 1).toLocaleString(lng, { month: "short" });
};
function PickerHeader({
  title,
  onPrev,
  canPrev,
  onNext,
  canNext,
  onPrevDisabledClick,
  onNextDisabledClick,
}) {
  return (
    <div className="flex items-center justify-between rounded-t-md bg-primary px-3 py-2 text-primary-foreground">
      <button
        type="button"
        onClick={() => {
          if (!canPrev) {
            onPrevDisabledClick?.();
            return;
          }

          onPrev?.();
        }}
        className={cn(
          "rounded p-1 hover:bg-white/20",
          !canPrev && "cursor-not-allowed opacity-30",
        )}
      >
        <ChevronLeft className="size-4" />
      </button>
      <span className="text-center text-sm font-medium">{title}</span>
      {onNext ? (
        <button
          type="button"
          onClick={() => {
            if (!canNext) {
              onNextDisabledClick?.();
              return;
            }

            onNext?.();
          }}
          className={cn(
            "rounded p-1 hover:bg-white/20",
            !canNext && "cursor-not-allowed opacity-30",
          )}
        >
          <ChevronRight className="size-4" />
        </button>
      ) : (
        <div className="size-6" />
      )}
    </div>
  );
}

export const YearMonthPicker = ({
  value,
  onChange,
  placeholder,
  minYear = 2018,
  minMonth = 1,
  maxYear = currentYear,
  disabled = false,
  onPrevDisabledClick,
  onNextDisabledClick,
}) => {
  const { t, lng } = useTran();
  const [open, setOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const resolvedPlaceholder = placeholder ?? t("Select year & month");
  const [rangeStart, setRangeStart] = useState(() => {
    const initial = Math.min(
      Math.max(value?.year ?? minYear, minYear),
      maxYear,
    );
    return getRangeStart(initial, minYear);
  });

  const stage = selectedYear !== null ? "month" : "year";

  const years = Array.from(
    { length: RANGE_SIZE },
    (_, i) => rangeStart + i,
  ).filter((year) => year >= minYear && year <= maxYear);

  const rangeTitle =
    years.length > 0
      ? `${years[0]} - ${years[years.length - 1]}`
      : `${minYear} - ${maxYear}`;

  const displayValue =
    value?.year && value?.month
      ? `${getMonths(value.month, lng)} ${value.year}`
      : null;

  const handleOpen = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSelectedYear(null);
    }
  };

  const handleMonthClick = (monthIndex) => {
    onChange({ year: selectedYear, month: monthIndex + 1 });
    setOpen(false);
    setSelectedYear(null);
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="border-input flex h-9 w-full min-w-0 items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            className={cn(
              "min-w-0 truncate text-left",
              !displayValue && "text-muted-foreground",
            )}
          >
            {displayValue || resolvedPlaceholder}
          </span>
          <ChevronDown className="size-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(13rem,calc(100vw-1rem))] p-0"
        align="start"
      >
        {stage === "year" ? (
          <div>
            <PickerHeader
              title={rangeTitle}
              onPrev={() => setRangeStart((current) => current - RANGE_SIZE)}
              canPrev={rangeStart > minYear}
              onNext={() => setRangeStart((current) => current + RANGE_SIZE)}
              canNext={rangeStart + RANGE_SIZE <= maxYear}
              onPrevDisabledClick={onPrevDisabledClick}
              onNextDisabledClick={onNextDisabledClick}
            />
            <div className="grid grid-cols-3 gap-1 p-2">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  onClick={() => setSelectedYear(year)}
                  className={cn(
                    "rounded py-1.5 text-sm transition-colors hover:bg-muted",
                    year === currentYear && "font-bold",
                    value?.year === year &&
                      "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <PickerHeader
              title={String(selectedYear)}
              onPrev={() => setSelectedYear(null)}
              canPrev
            />
            <div className="grid grid-cols-3 gap-1 p-2">
              {Month_Numbers.map((monthNumber, index) => {
                const isDisabled =
                  selectedYear === minYear && monthNumber < minMonth;

                return (
                  <button
                    key={monthNumber}
                    type="button"
                    onClick={() => handleMonthClick(index)}
                    disabled={isDisabled}
                    className={cn(
                      "rounded py-1.5 text-sm transition-colors hover:bg-muted",
                      isDisabled &&
                        "cursor-not-allowed opacity-30 hover:bg-transparent",
                      value?.year === selectedYear &&
                        value?.month === monthNumber &&
                        "bg-primary text-primary-foreground hover:bg-primary/90",
                    )}
                  >
                    {getMonths(monthNumber, lng)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const getEndDateRestrictionMessage = ({
  startYear,
  minUsedYear,
  maxUsedYear,
}) => {
  if (!minUsedYear || !maxUsedYear) {
    return `The end year must be later than the start year(${startYear}).`;
  }

  return `The end date cannot be earlier than ${maxUsedYear} because reporting data already exists in the period year(s).`;
};

export const getStartDateRestrictionMessage = ({
  maxYear,
  minUsedYear,
  maxUsedYear,
}) => {
  if (!minUsedYear || !maxUsedYear) {
    return `The start date must be earlier than ${maxYear}.`;
  }
  return `The start date must be ${maxYear} or earlier because reporting data already exists starting in ${maxYear}.`;
};
